import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Loader2, ExternalLink, Gift, Clock, CheckCircle2, Ban, PauseCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";

const STATUS_META = {
  trialing: { label: "En prueba", cls: "bg-blue-50 text-blue-700", icon: Clock },
  active: { label: "Activa", cls: "bg-green-50 text-green-700", icon: CheckCircle2 },
  free: { label: "Cortesía", cls: "bg-amber-50 text-amber-700", icon: Gift },
  paused: { label: "En pausa", cls: "bg-stone-100 text-stone-600", icon: PauseCircle },
  canceled: { label: "Cancelada", cls: "bg-red-50 text-red-700", icon: Ban },
  cancelled: { label: "Cancelada", cls: "bg-red-50 text-red-700", icon: Ban },
  past_due: { label: "Pago pendiente", cls: "bg-red-50 text-red-700", icon: Ban },
  none: { label: "Sin suscripción", cls: "bg-stone-100 text-stone-600", icon: Ban },
};

const fmtDate = (unixOrIso) => {
  if (!unixOrIso) return null;
  try {
    const d = typeof unixOrIso === "number" ? new Date(unixOrIso * 1000) : new Date(unixOrIso);
    return d.toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return null; }
};

export default function SubscriptionCard() {
  const [sub, setSub] = useState(undefined); // undefined = loading
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/billing/subscription").then(({ data }) => setSub(data)).catch(() => setSub(null));
  }, []);

  if (sub === undefined) {
    return (
      <div className="mt-8 rounded-2xl bg-white border border-xuma-border p-6 grid place-items-center">
        <Loader2 className="h-5 w-5 animate-spin text-xuma-primary" />
      </div>
    );
  }
  // Hide entirely if the account has no billing relationship at all.
  if (!sub || !sub.has_subscription) return null;

  const statusKey = sub.free_active ? "free" : (sub.subscription_status || "none");
  const meta = STATUS_META[statusKey] || STATUS_META.none;
  const StatusIcon = meta.icon;

  // Next relevant date to communicate to the client.
  let nextLabel = "Próximo cobro";
  let nextDate = fmtDate(sub.current_period_end);
  if (sub.free_active) { nextLabel = "Cortesía hasta"; nextDate = fmtDate(sub.free_until); }
  else if (sub.subscription_status === "trialing") { nextLabel = "Primer cobro mensual"; nextDate = fmtDate(sub.trial_end); }
  else if (sub.pause_resumes_at) { nextLabel = "Se reanuda el"; nextDate = fmtDate(sub.pause_resumes_at); }

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.href });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 rounded-2xl bg-white border border-xuma-border p-6" data-testid="subscription-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-xuma-primary/10 grid place-items-center">
            <CreditCard className="h-5 w-5 text-xuma-primary" />
          </span>
          <div>
            <h3 className="font-heading font-bold text-xuma-dark">Mi suscripción</h3>
            <p className="text-sm text-xuma-muted">
              {sub.plan_label || "Tu plan"}{sub.monthly_fee != null ? ` · ${money(sub.monthly_fee)} / mes` : ""}
            </p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 font-medium ${meta.cls}`} data-testid="subscription-status">
          <StatusIcon className="h-3.5 w-3.5" /> {meta.label}
        </span>
      </div>

      {nextDate && (
        <div className="mt-4 rounded-xl bg-xuma-dash border border-xuma-border px-4 py-3 text-sm flex items-center justify-between">
          <span className="text-xuma-muted">{nextLabel}</span>
          <span className="font-medium text-xuma-dark" data-testid="subscription-next-date">{nextDate}</span>
        </div>
      )}
      {sub.cancel_at_period_end && (
        <p className="mt-3 text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2">
          Tu suscripción se cancelará al final del periodo actual.
        </p>
      )}

      <Button data-testid="manage-subscription-btn" onClick={openPortal} disabled={busy}
        className="mt-5 w-full h-11 rounded-full bg-xuma-dark hover:bg-black text-white gap-2">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
        Gestionar suscripción y método de pago
      </Button>
      <p className="mt-2 text-[11px] text-xuma-muted text-center">
        Actualiza tu tarjeta, revisa tus facturas o cancela cuando quieras.
      </p>
    </div>
  );
}
